import { Box, Divider, HStack, Text, VStack } from "@chakra-ui/react";

// Add typehint GMW to wellData prop
type VisualisationTube = {
  tubeTopPosition: number;
  plainTubePartLength: number;
  screenLength: number;
  sedimentSumpLength?: number;
};

type WellData = {
  wellHeadProtector: string;
  groundLevelPosition: number;
  monitoringTubes: VisualisationTube[];
};

export const MonitoringWellVisualization = ({ wellData }: { wellData: WellData }) => {
  const { wellHeadProtector, groundLevelPosition, monitoringTubes } = wellData;

  // Calculate the total depth range for scaling
  const calculateDepthRange = () => {
    let minDepth = groundLevelPosition;
    let maxDepth = groundLevelPosition;

    monitoringTubes.forEach((tube: VisualisationTube) => {
      const tubeTop = tube.tubeTopPosition;
      const tubeBottom = tubeTop - tube.plainTubePartLength;
      const filterBottom = tubeBottom - tube.screenLength;
      const sumpBottom = filterBottom - (tube.sedimentSumpLength || 0);
      minDepth = Math.min(minDepth, sumpBottom);
      maxDepth = Math.max(maxDepth, tubeTop);
    });

    return { minDepth, maxDepth, range: maxDepth - minDepth };
  };

  const { maxDepth, range } = calculateDepthRange();

  const VISUALIZATION_HEIGHT = 500; // pixels
  const SCALE = VISUALIZATION_HEIGHT / range;

  // Convert mNAP position to pixel position
  const napToPixel = (napPosition: number) => {
    return (maxDepth - napPosition) * SCALE;
  };

  const FilterColumn = ({ filter }: { filter: VisualisationTube }) => {
    const tubeTop = filter.tubeTopPosition;
    const plainTubeBottom = tubeTop - filter.plainTubePartLength;
    const filterBottom = plainTubeBottom - filter.screenLength;
    const sumpBottom = filterBottom - (filter.sedimentSumpLength || 0);

    const tubeTopY = napToPixel(tubeTop);
    const plainTubeBottomY = napToPixel(plainTubeBottom);
    const filterBottomY = napToPixel(filterBottom);
    const sumpBottomY = napToPixel(sumpBottom);

    return (
      <Box position="relative" width="80px" h={`${VISUALIZATION_HEIGHT}px`}>
        {/* Tube Top */}
        {!!tubeTop && (
          <>
            <Box
              position="absolute"
              top={`${tubeTopY}px`}
              left="50%"
              transform="translateX(-50%)"
              width="40px"
              height="8px"
              bg="gray.700"
              borderRadius="md"
            />
            <Text
              position="absolute"
              top={`${tubeTopY}px`}
              left="65px"
              fontSize="xs"
              whiteSpace="nowrap"
            >
              {tubeTop.toFixed(2)} mNAP
            </Text>
          </>
        )}

        {/* Plain Tube */}
        {!!filter.plainTubePartLength && (
          <>
            <Box
              position="absolute"
              top={`${tubeTopY + 8}px`}
              left="50%"
              transform="translateX(-50%)"
              width="20px"
              height={`${plainTubeBottomY - tubeTopY - 8}px`}
              bg="gray.400"
              border="2px solid"
              borderColor="gray.600"
            />
            <Text
              position="absolute"
              top={`${(tubeTopY + plainTubeBottomY) / 2}px`}
              left="20px"
              transform="translateX(-100%)"
              fontSize="xs"
              color="gray.600"
              whiteSpace="nowrap"
            >
              Peilbuis: {filter.plainTubePartLength}m
            </Text>{" "}
          </>
        )}

        {/* Filter */}
        {!!filter.screenLength && (
          <>
            <Box
              position="absolute"
              top={`${plainTubeBottomY}px`}
              left="50%"
              transform="translateX(-50%)"
              width="20px"
              height={`${filterBottomY - plainTubeBottomY}px`}
              bg="blue.200"
              border="2px solid"
              borderColor="blue.500"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(66, 153, 225, 0.3) 2px, rgba(66, 153, 225, 0.3) 4px)",
              }}
            />
            <Text
              position="absolute"
              top={`${(plainTubeBottomY + filterBottomY) / 2}px`}
              left="15px"
              transform="translateX(-100%)"
              fontSize="xs"
              color="blue.600"
              whiteSpace="nowrap"
            >
              Filter: {filter.screenLength}m
            </Text>
          </>
        )}

        {/* Sediment Sump (if exists) only do this if not undefined */}
        {filter.sedimentSumpLength !== undefined && filter.sedimentSumpLength > 0 && (
          <>
            <Box
              position="absolute"
              top={`${filterBottomY}px`}
              left="50%"
              transform="translateX(-50%)"
              width="20px"
              height={`${sumpBottomY - filterBottomY}px`}
              bg="brown.300"
              border="2px solid"
              borderColor="brown.600"
            />
            <Text
              position="absolute"
              top={`${(filterBottomY + sumpBottomY) / 2}px`}
              left="15px"
              transform="translateX(-100%)"
              fontSize="xs"
              color="brown.600"
              whiteSpace="nowrap"
            >
              Zandvang: {filter.sedimentSumpLength}m
            </Text>
          </>
        )}

        {/* Bottom cap */}
        {!!sumpBottom && (
          <>
            <Box
              position="absolute"
              top={`${sumpBottomY}px`}
              left="50%"
              transform="translateX(-50%)"
              width="24px"
              height="6px"
              bg="gray.700"
              borderRadius="0 0 md md"
            />
            <Text
              position="absolute"
              top={`${sumpBottomY}px`}
              left="15px"
              transform="translate(-100%, -50%)"
              fontSize="xs"
              whiteSpace="nowrap"
            >
              {sumpBottom.toFixed(2)} mNAP
            </Text>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      p={6}
      h="fit-content"
      bg="gray.50"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
    >
      <VStack spacing={4} alignItems="flex-start">
        {/* Header */}
        <Box>
          <Text fontSize="lg" fontWeight="bold">
            Peilbuis visualisatie
          </Text>
          <Text fontSize="sm" color="gray.600">
            Beschermconstructie: {wellHeadProtector}
          </Text>
        </Box>
        <Box width="100%">
          <HStack justifyContent="space-between" width="100%">
            <Text fontSize="sm" fontWeight="bold" ml="20px">
              Maaiveld
            </Text>
            <Text fontSize="sm" fontWeight="bold">
              filter{monitoringTubes.length > 1 ? "s" : ""} ({monitoringTubes.length})
            </Text>
          </HStack>
        </Box>
        <Divider />
        {/* Visualization Area */}
        <Box width="400px" h="fit-content" position="relative" overflowX="auto">
          <HStack
            spacing={8}
            alignItems="flex-start"
            minWidth="fit-content"
            // Add a little padding seeing that the top and bottom text will otherwise be hidden
            py="10px"
            height={`${VISUALIZATION_HEIGHT + 20}px`}
          >
            {/* Ground Level Reference */}
            <Box position="relative" width="150px">
              <Box
                position="absolute"
                top={`${napToPixel(groundLevelPosition)}px`}
                left="0"
                right="0"
                height="4px"
                bg="green.600"
                borderRadius="sm"
              />
              <Text
                position="absolute"
                top={`${napToPixel(groundLevelPosition) + 5}px`}
                left="0"
                fontSize="sm"
                fontWeight="bold"
                color="green.700"
              >
                Maaiveldpositie
              </Text>
              <Text
                position="absolute"
                top={`${napToPixel(groundLevelPosition) + 25}px`}
                left="0"
                fontSize="xs"
                color="green.700"
              >
                {groundLevelPosition.toFixed(2)} mNAP
              </Text>
            </Box>

            {/* Filter Columns */}
            <HStack spacing={12} alignItems="flex-start" height={`${VISUALIZATION_HEIGHT}px`}>
              {monitoringTubes.map((tube: VisualisationTube, index: number) => (
                <FilterColumn key={index} filter={tube} />
              ))}
            </HStack>
          </HStack>
        </Box>
        {/* Legend */}
        <Box mt={4} p={3} bg="white" borderRadius="md" width="100%">
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Legenda:
          </Text>
          <HStack spacing={6} flexWrap="wrap">
            <HStack spacing={2}>
              <Box
                width="20px"
                height="10px"
                bg="gray.400"
                border="2px solid"
                borderColor="gray.600"
              />
              <Text fontSize="xs">Peilbuis</Text>
            </HStack>
            <HStack spacing={2}>
              <Box
                width="20px"
                height="10px"
                bg="blue.200"
                border="2px solid"
                borderColor="blue.500"
              />
              <Text fontSize="xs">Filter</Text>
            </HStack>
            <HStack spacing={2}>
              <Box
                width="20px"
                height="10px"
                bg="brown.300"
                border="2px solid"
                borderColor="brown.600"
              />
              <Text fontSize="xs">Zandvang</Text>
            </HStack>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

// Example usage:
// <MonitoringWellVisualization
//   wellData={{
//     wellHeadProtector: "Type A Protection",
//     groundLevelPosition: 2.5,
//     filters: [
//       {
//         tubeTopPosition: 3.0,
//         plainTubeLength: 5.0,
//         filterLength: 2.0,
//         sedimentSumpLength: 0.5
//       },
//       {
//         tubeTopPosition: 2.8,
//         plainTubeLength: 8.0,
//         filterLength: 3.0,
//         sedimentSumpLength: 0
//       }
//     ]
//   }}
// />
